
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { DocumentType } from "./types";

export function useDocumentCreation() {
  const [isCreating, setIsCreating] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const getInitialContent = (type: DocumentType) => {
    switch (type) {
      case "okr":
        return {
          objectives: "",
          keyResults: "",
          timeline: "",
          progressTracking: ""
        };
      case "market_research":
        return {
          executiveSummary: "",
          marketOverview: "",
          targetMarketAnalysis: "",
          competitorAnalysis: "",
          marketTrends: ""
        };
      case "strategic_plan":
        return {
          executiveSummary: "",
          missionStatement: "",
          visionStatement: "",
          strategicGoals: ""
        };
      case "pitch_deck":
        return {
          problemStatement: "",
          solution: "",
          marketOpportunity: "",
          businessModel: "",
          competitiveAdvantage: "",
          financialProjections: ""
        };
      case "business_model_canvas":
        return {
          keyPartners: "",
          keyActivities: "",
          valuePropositions: "",
          customerRelationships: "",
          customerSegments: "",
          keyResources: "",
          channels: "",
          costStructure: "",
          revenueStreams: ""
        };
      case "business_plan":
        return {
          executiveSummary: "",
          companyDescription: "",
          marketAnalysis: "",
          organizationManagement: "",
          serviceProductLine: "",
          marketingSalesStrategy: "",
          fundingRequirements: "",
          financialProjections: ""
        };
      case "customer_acquisition_plan":
        return {
          targetCustomerAnalysis: "",
          acquisitionStrategy: "",
          retentionPrograms: "",
          customerJourneyMap: "",
          metricsAndKpis: ""
        };
      case "go_to_market_strategy":
        return {
          marketAnalysis: "",
          targetAudience: "",
          positioningStrategy: "",
          channelStrategy: "",
          launchPlan: ""
        };
      case "swot_analysis":
        return {
          strengths: "",
          weaknesses: "",
          opportunities: "",
          threats: ""
        };
      case "kpi_creation":
        return {
          businessObjectives: "",
          financialKpis: "",
          customerKpis: "",
          operationalKpis: "",
          implementationPlan: ""
        };
      case "innovation_roadmap":
        return {
          currentStateAssessment: "",
          innovationGoals: "",
          focusAreas: "",
          timelineMilestones: "",
          resourceRequirements: "",
          successMetrics: ""
        };
      case "sop":
        return {
          purposeScope: "",
          definitions: "",
          responsibilities: "",
          procedureSteps: "",
          qualityControl: "",
          references: ""
        };
      // Financial Documents (CFO)
      case "financial_forecast":
        return {
          revenueProjections: "",
          expenseForecasts: "",
          cashFlowProjections: "",
          profitabilityAnalysis: ""
        };
      case "budget_template":
        return {
          budgetSummary: "",
          departmentalBudgets: "",
          capitalExpenditures: "",
          varianceAnalysis: ""
        };
      case "cash_flow_statement":
        return {
          operatingActivities: "",
          investingActivities: "",
          financingActivities: "",
          netCashFlow: ""
        };
      case "income_statement":
        return {
          revenue: "",
          costOfGoodsSold: "",
          operatingExpenses: "",
          netIncome: ""
        };
      case "balance_sheet":
        return {
          assets: "",
          liabilities: "",
          equity: "",
          financialRatios: ""
        };
      case "financial_dashboard":
        return {
          keyMetrics: "",
          performanceIndicators: "",
          trendAnalysis: "",
          riskAssessment: ""
        };
      // Technology Documents (CTO)
      case "tech_architecture":
        return {
          systemOverview: "",
          technologyStack: "",
          infrastructureDesign: "",
          scalabilityPlan: ""
        };
      case "cybersecurity_policy":
        return {
          securityFramework: "",
          accessControls: "",
          dataProtection: "",
          incidentResponse: ""
        };
      case "digital_transformation_plan":
        return {
          currentStateAssessment: "",
          futureStateVision: "",
          transformationRoadmap: "",
          changeManagement: ""
        };
      // Operations Documents (COO)
      case "process_optimization":
        return {
          currentProcessAnalysis: "",
          inefficiencyIdentification: "",
          optimizationRecommendations: "",
          implementationPlan: ""
        };
      case "quality_management":
        return {
          qualityStandards: "",
          qualityControl: "",
          continuousImprovement: "",
          complianceRequirements: ""
        };
      case "operations_manual":
        return {
          operationalOverview: "",
          departmentalProcedures: "",
          emergencyProcedures: "",
          performanceMetrics: ""
        };
      // Product Documents (CPO)
      case "product_roadmap":
        return {
          productVision: "",
          featurePrioritization: "",
          releaseTimeline: "",
          resourceRequirements: ""
        };
      case "user_research_plan":
        return {
          researchObjectives: "",
          targetAudience: "",
          researchMethods: "",
          dataAnalysis: ""
        };
      case "product_requirements":
        return {
          functionalRequirements: "",
          userStories: "",
          acceptanceCriteria: "",
          technicalConstraints: ""
        };
      // Contract Documents
      case "consulting_agreement":
        return {
          consultantInfo: "",
          clientInfo: "",
          servicesDescription: "",
          deliverables: "",
          timeline: "",
          compensation: "",
          confidentiality: ""
        };
      case "media_release":
        return {
          releaseInfo: "",
          subjectInfo: "",
          usageRights: "",
          compensation: "",
          releaseTerms: ""
        };
      case "nda_agreement":
        return {
          parties: "",
          confidentialInfo: "",
          obligations: "",
          exceptions: "",
          duration: "",
          remedies: ""
        };
      case "service_contract":
        return {
          overview: "",
          parties: "",
          terms: "",
          paymentTerms: "",
          scopeOfWork: "",
          termination: "",
          legalProvisions: ""
        };
      default:
        return {};
    }
  };

  const createDocument = async (type: DocumentType, title: string) => {
    try {
      setIsCreating(true);
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("No active session");
      }

      const content = getInitialContent(type);
      
      // Cast type to the database enum type to avoid TypeScript issues
      const { data: document, error } = await supabase
        .from("documents")
        .insert({
          user_id: session.user.id,
          type: type as any, // Cast to bypass type checking since enum was just updated
          title,
          content
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Success",
        description: "Document created successfully"
      });

      navigate(`/documents/${document.id}`);
    } catch (error: any) {
      console.error("Error creating document:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to create document",
        variant: "destructive"
      });
    } finally {
      setIsCreating(false);
    }
  };

  return {
    createDocument,
    isCreating
  };
}
